import { Input as InputPrimitive } from "@base-ui/react/input";
import { cn } from "@timeghost/ui/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

const inputVariants = cva(
	"w-full min-w-0 text-ellipsis text-base outline-none transition-[color,background-color,border-color,box-shadow] file:inline-flex file:h-6 file:border-0 file:bg-transparent file:font-medium file:text-foreground file:text-sm placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-destructive/20 md:text-sm dark:aria-invalid:ring-destructive/40",
	{
		variants: {
			variant: {
				default:
					"rounded-lg border border-input bg-transparent px-2.5 py-1 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:bg-input/50 aria-invalid:border-destructive aria-invalid:ring-3 dark:bg-input/40 dark:aria-invalid:border-destructive/50 dark:disabled:bg-input/80",
				/** Material-like filled field. Pair with `rounded-none` for edge-to-edge dialog search. */
				filled:
					"rounded-md border-0 border-border border-b bg-accent px-3 py-2 shadow-none focus-visible:border-ring focus-visible:border-b-2 focus-visible:bg-input focus-visible:ring-0 disabled:bg-well aria-invalid:border-destructive aria-invalid:border-b-2 dark:bg-accent dark:focus-visible:bg-input",
				ghost:
					"rounded-md border-0 border-border border-b bg-transparent px-3 py-2 shadow-none focus-visible:border-muted-foreground focus-visible:bg-input focus-visible:ring-0 disabled:bg-well aria-invalid:border-destructive aria-invalid:border-b-2 dark:bg-transparent dark:focus-visible:bg-input",
				underline:
					"rounded-none border-0 border-border border-b-2 bg-transparent px-0 py-1 shadow-none focus-visible:border-foreground focus-visible:ring-0 disabled:bg-transparent aria-invalid:border-destructive dark:bg-transparent",
				/** Material Web filled text field. Error via `aria-invalid`. */
				"md-filled":
					"rounded-t rounded-b-none border-0 border-border border-b bg-input py-2 text-foreground caret-primary shadow-none in-[[data-slot=field]]:placeholder:opacity-0 hover:border-foreground hover:bg-accent focus:border-primary focus:border-b-[3px] focus:bg-input in-[[data-slot=field]]:focus:placeholder:opacity-100 focus-visible:border-primary focus-visible:border-b-[3px] focus-visible:bg-input focus-visible:ring-0 focus-visible:ring-offset-0 in-[[data-slot=field]]:focus-visible:placeholder:opacity-100 disabled:border-border disabled:bg-input disabled:hover:border-border disabled:hover:bg-input aria-invalid:border-destructive aria-invalid:caret-destructive aria-invalid:ring-0 disabled:aria-invalid:border-border disabled:aria-invalid:bg-input disabled:aria-invalid:caret-current aria-invalid:focus-visible:border-destructive aria-invalid:focus-visible:border-b-[3px] aria-invalid:focus-visible:bg-input aria-invalid:focus:border-destructive aria-invalid:focus:border-b-[3px] aria-invalid:focus:bg-input aria-invalid:hover:border-destructive aria-invalid:hover:bg-destructive/10 dark:aria-invalid:ring-0",
			},
			inputSize: {
				/** Matches `Select` size="sm" (h-8) for dense settings rows. */
				sm: "h-8",
				default: "h-9",
				lg: "h-10",
				xl: "h-12",
				"2xl": "h-14",
			},
		},
		compoundVariants: [
			{
				variant: "md-filled",
				inputSize: ["sm", "default"],
				class:
					"px-3 in-[[data-slot=field]:has([data-slot=field-label]:not(.sr-only))]:pt-3.5 in-[[data-slot=field]:has([data-slot=field-label]:not(.sr-only))]:pb-1",
			},
			{
				variant: "md-filled",
				inputSize: "lg",
				class:
					"px-3.5 in-[[data-slot=field]:has([data-slot=field-label]:not(.sr-only))]:pt-4 in-[[data-slot=field]:has([data-slot=field-label]:not(.sr-only))]:pb-1.5",
			},
			{
				variant: "md-filled",
				inputSize: "xl",
				class:
					"px-4 in-[[data-slot=field]:has([data-slot=field-label]:not(.sr-only))]:pt-4.5 in-[[data-slot=field]:has([data-slot=field-label]:not(.sr-only))]:pb-2",
			},
			{
				variant: "md-filled",
				inputSize: "2xl",
				class:
					"px-4 in-[[data-slot=field]:has([data-slot=field-label]:not(.sr-only))]:pt-5 in-[[data-slot=field]:has([data-slot=field-label]:not(.sr-only))]:pb-2",
			},
		],
		defaultVariants: {
			variant: "default",
			inputSize: "default",
		},
	}
);

function Input({
	className,
	type,
	variant = "default",
	inputSize: size,
	autoComplete = "off",
	ariaAutocomplete = "none",
	placeholder,
	...props
}: React.ComponentProps<"input"> &
	VariantProps<typeof inputVariants> & {
		ariaAutocomplete?: "none" | "inline" | "list" | "both";
	}) {
	return (
		<InputPrimitive
			aria-autocomplete={ariaAutocomplete}
			autoComplete={autoComplete}
			className={cn(inputVariants({ variant, inputSize: size }), className)}
			data-size={size ?? "default"}
			data-slot="input"
			data-variant={variant}
			placeholder={placeholder ?? (variant === "md-filled" ? " " : undefined)}
			type={type}
			{...props}
		/>
	);
}

export { Input, inputVariants };
